/**
 * EditableBuildPhaseSection - Build Phase steps with element-level editing
 * 
 * Each element (heading, subheading, step items) is wrapped with SelectableElement
 * for click-to-select in the Page Builder Studio.
 */
import React, { useRef, useEffect, useMemo } from 'react';
import SelectableElement from './SelectableElement';
import InlineTextEditor from './InlineTextEditor';
import { 
  LANDING_BUILD_PHASE_ELEMENTS,
  getElementValue,
  getElementStyles,
  getElementMotion,
} from './ElementRegistry';

// Easing CSS values
const EASING_VALUES = {
  smooth: 'cubic-bezier(0.16, 1, 0.3, 1)',
  calm: 'cubic-bezier(0.4, 0, 0.2, 1)',
  snappy: 'cubic-bezier(0.22, 1, 0.36, 1)',
};

// Element animation wrapper
const AnimatedElement = ({ 
  children, 
  motion,
  animationKey,
  disabled,
  className = '' 
}) => {
  const ref = useRef(null);
  const prevKeyRef = useRef(animationKey);
  
  const { durationMs = 700, delayMs = 0, easing = 'smooth', fromOpacity = 0, fromY = 30, fromScale = 1 } = motion || {};
  const easingValue = EASING_VALUES[easing] || EASING_VALUES.smooth;

  useEffect(() => {
    if (animationKey !== prevKeyRef.current && ref.current) {
      prevKeyRef.current = animationKey;
      ref.current.style.opacity = fromOpacity;
      ref.current.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
      void ref.current.offsetWidth;
      setTimeout(() => {
        if (ref.current) {
          ref.current.style.opacity = '1';
          ref.current.style.transform = 'translateY(0) scale(1)';
        }
      }, 50);
    }
  }, [animationKey, fromOpacity, fromY, fromScale]);

  useEffect(() => {
    if (disabled || !ref.current) return;
    const el = ref.current;
    el.style.opacity = fromOpacity;
    el.style.transform = `translateY(${fromY}px) scale(${fromScale})`;
    el.style.transition = `opacity ${durationMs}ms ${easingValue} ${delayMs}ms, transform ${durationMs}ms ${easingValue} ${delayMs}ms`;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0) scale(1)';
        observer.disconnect();
      }
    }, { threshold: 0.1 });
    
    observer.observe(el);
    return () => observer.disconnect();
  }, [disabled, durationMs, delayMs, easingValue, fromOpacity, fromY, fromScale]);

  if (disabled) return <div className={className}>{children}</div>;
  return <div ref={ref} className={className}>{children}</div>;
};

const getTextStyleClass = (styles) => {
  const sizeMap = {
    'xs': 'text-xs', 'sm': 'text-sm', 'base': 'text-base', 'lg': 'text-lg',
    'xl': 'text-xl', '2xl': 'text-2xl', '3xl': 'text-3xl', '4xl': 'text-4xl',
  };
  const weightMap = {
    'light': 'font-light', 'normal': 'font-normal', 'medium': 'font-medium',
    'semibold': 'font-semibold', 'bold': 'font-bold',
  };
  const alignMap = { 'left': 'text-left', 'center': 'text-center', 'right': 'text-right' };
  
  return `${sizeMap[styles?.size] || 'text-base'} ${weightMap[styles?.weight] || ''} ${alignMap[styles?.alignment] || 'text-center'}`;
};

const EditableBuildPhaseSection = ({ 
  props = {},
  selectedElementId,
  onSelectElement,
  editingElementId,
  onStartEditing,
  onStopEditing,
  onUpdateElementValue,
  animationKey = 0,
  disabled = false,
}) => {
  const elements = useMemo(() => {
    const map = {};
    LANDING_BUILD_PHASE_ELEMENTS.forEach(el => { map[el.id] = el; });
    return map;
  }, []);

  const getValue = (elementId) => {
    const element = elements[elementId];
    if (!element) return '';
    return getElementValue(props, element);
  };

  const getStyles = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementStyles(props, element);
  };

  const getMotion = (elementId) => {
    const element = elements[elementId];
    if (!element) return {};
    return getElementMotion(props, element);
  };

  const handleSelectElement = (elementId) => onSelectElement?.(elementId);
  const handleDoubleClick = (elementId) => onStartEditing?.(elementId);
  const handleSaveText = (elementId, newValue) => {
    onUpdateElementValue?.(elementId, newValue);
    onStopEditing?.();
  };

  // Safe string coercion - ensures we never render objects as React children
  const safeString = (val, fallback = '') => {
    if (!val) return fallback;
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      // Handle common object shapes from database
      if (val.title) return `${val.title}${val.description ? `: ${val.description}` : ''}`;
      if (val.label) return val.label;
      if (val.text) return val.text;
    }
    return fallback;
  };

  // Heading
  const headingValue = safeString(getValue('build_phase.heading'), 'This is the build phase');
  const headingStyles = getStyles('build_phase.heading');
  const headingMotion = getMotion('build_phase.heading');

  // Subheading
  const subheadingValue = safeString(getValue('build_phase.subheading'), props.description || 'Core features launching now');
  const subheadingStyles = getStyles('build_phase.subheading');
  const subheadingMotion = getMotion('build_phase.subheading');

  // Default steps
  const defaultSteps = [
    'Episode uploads with page-by-page reader',
    'Chapter-based novel publishing',
    'Creator profiles with portfolio view'
  ];

  const steps = props.steps || defaultSteps;

  // Helper to normalize step text
  const normalizeStep = (step) => {
    if (typeof step === 'string') return step;
    if (typeof step === 'object' && step.title) {
      return `${step.title}${step.description ? `: ${step.description}` : ''}`;
    }
    return '';
  };

  return (
    <section className="py-24 bg-zinc-950" data-testid="build-phase-section">
      <div className="max-w-4xl mx-auto px-6 text-center">
        {/* Heading */}
        <AnimatedElement
          motion={headingMotion}
          animationKey={animationKey}
          disabled={disabled}
        >
          <SelectableElement
            elementId="build_phase.heading"
            elementLabel="Section Heading"
            elementType="text"
            isSelected={selectedElementId === 'build_phase.heading'}
            onSelect={handleSelectElement}
            disabled={disabled}
          >
            <h2 
              className={`text-white mb-4 ${getTextStyleClass(headingStyles)}`}
              style={{ opacity: headingStyles.opacity ?? 1 }}
              onDoubleClick={() => handleDoubleClick('build_phase.heading')}
            >
              {editingElementId === 'build_phase.heading' ? (
                <InlineTextEditor
                  value={headingValue}
                  isEditing={true}
                  maxLength={60}
                  onSave={(val) => handleSaveText('build_phase.heading', val)}
                  onCancel={onStopEditing}
                  className="inline"
                />
              ) : (
                headingValue
              )}
            </h2>
          </SelectableElement>
        </AnimatedElement>

        {/* Subheading */}
        <AnimatedElement
          motion={subheadingMotion}
          animationKey={animationKey}
          disabled={disabled}
          className="mb-12"
        >
          <SelectableElement
            elementId="build_phase.subheading"
            elementLabel="Subheading"
            elementType="text"
            isSelected={selectedElementId === 'build_phase.subheading'}
            onSelect={handleSelectElement}
            disabled={disabled}
          >
            <p 
              className={`text-zinc-400 ${getTextStyleClass(subheadingStyles)}`}
              style={{ opacity: subheadingStyles.opacity ?? 0.7 }}
              onDoubleClick={() => handleDoubleClick('build_phase.subheading')}
            >
              {editingElementId === 'build_phase.subheading' ? (
                <InlineTextEditor
                  value={subheadingValue}
                  isEditing={true}
                  maxLength={100}
                  onSave={(val) => handleSaveText('build_phase.subheading', val)}
                  onCancel={onStopEditing}
                  className="inline"
                />
              ) : (
                subheadingValue
              )}
            </p>
          </SelectableElement>
        </AnimatedElement>
        
        {/* Step Items */}
        <div className="space-y-4">
          {steps.map((step, index) => {
            const elementId = `build_phase.step_${index}`;
            const stepMotion = getMotion(elementId);
            const stepStyles = getStyles(elementId);
            const stepValue = getValue(elementId);
            // Normalize the step value - it could be a string or an object with {title, description}
            const displayText = safeString(stepValue) || normalizeStep(step);
            
            return (
              <AnimatedElement 
                key={`step-${index}`}
                motion={stepMotion}
                animationKey={animationKey}
                disabled={disabled}
              >
                <SelectableElement
                  elementId={elementId}
                  elementLabel={`Step ${index + 1}`}
                  elementType="text"
                  isSelected={selectedElementId === elementId}
                  onSelect={handleSelectElement}
                  disabled={disabled}
                >
                  <div 
                    className="flex items-center gap-4 bg-zinc-900/50 border border-zinc-800 rounded-xl px-6 py-4 text-left"
                    style={{ opacity: stepStyles.opacity ?? 1 }}
                    onDoubleClick={() => handleDoubleClick(elementId)}
                  >
                    <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-orange-400 font-semibold">
                        {typeof step === 'object' && step.number ? step.number : index + 1}
                      </span>
                    </div>
                    {editingElementId === elementId ? (
                      <InlineTextEditor
                        value={displayText}
                        isEditing={true}
                        maxLength={80}
                        onSave={(val) => handleSaveText(elementId, val)}
                        onCancel={onStopEditing}
                        className="text-white flex-1"
                      />
                    ) : (
                      <span className="text-white">{displayText}</span>
                    )}
                  </div>
                </SelectableElement>
              </AnimatedElement>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EditableBuildPhaseSection;
